import React, { createContext, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { FACEBOOK_APP_ID, FACEBOOK_SDK_SCRIPT_TAG_ID } from '../config';
import { waitForGlobal } from './utils';
import { insertFacebookScriptElement } from './insertFacebookScriptElement';
import { useConsentLevel } from '../../common/hooks/useConsentLevel';
import { Logger } from '../../../utils';
const FacebookConnectionContext = createContext(undefined);
export const useFacebookConnection = () => {
    const context = useContext(FacebookConnectionContext);
    if (!context) {
        Logger.warn("No 'FacebookConnectionProvider' found when calling 'useFacebookConnection'");
        return {
            login: () => null,
            status: 'initialisationFailed',
            authData: { userId: null, accessToken: null },
        };
    }
    return context;
};
export const FacebookConnectionProvider = ({ children, }) => {
    const { isFunctional: consentGiven } = useConsentLevel();
    const timeoutIdRef = useRef(undefined);
    const [status, setStatus] = useState('awaitingConsent');
    const [authData, setAuthData] = useState({
        userId: null,
        accessToken: null,
    });
    const statusChangeCallback = useCallback((response) => {
        Logger.info('Facebook status retrieved: %o', response);
        if (response.status === 'connected') {
            setAuthData({
                userId: response.authResponse.userID,
                accessToken: response.authResponse.accessToken || null,
            });
        }
        else {
            setAuthData({ userId: null, accessToken: null });
        }
        setStatus(response.status);
    }, []);
    const login = useCallback(() => {
        Logger.info('Initiated login to Facebook');
        setStatus('loading');
        FB.login(statusChangeCallback, { scope: 'email', return_scopes: true });
    }, [statusChangeCallback]);
    const getLoginStatus = useCallback(() => {
        const forceReload = true;
        FB.getLoginStatus(statusChangeCallback, forceReload);
    }, [statusChangeCallback]);
    useEffect(() => {
        if (consentGiven && status === 'awaitingConsent') {
            setStatus('initialising');
        }
    }, [status, consentGiven]);
    useEffect(() => {
        if (!consentGiven)
            return;
        if (document.getElementById(FACEBOOK_SDK_SCRIPT_TAG_ID)) {
            Logger.info('Facebook SDK Script tag already exists');
            waitForGlobal('FB', 5000, timeoutIdRef)
                .then(() => {
                Logger.info('window.FB found');
                getLoginStatus();
            })
                .catch((err) => {
                Logger.error('Unable to find window.FB; %o', err);
                setStatus('initialisationFailed');
            });
            return;
        }
        const onError = () => {
            setStatus('initialisationFailed');
            Logger.error('Facebook SDK has failed to load');
        };
        Logger.info('Inserting Facebook SDK script tag');
        const cleanupEventListeners = insertFacebookScriptElement(onError);
        window.fbAsyncInit = () => {
            FB.init({ appId: FACEBOOK_APP_ID, version: 'v16.0' });
            getLoginStatus();
        };
        return () => {
            cleanupEventListeners();
            clearTimeout(timeoutIdRef.current);
        };
    }, [getLoginStatus, consentGiven]);
    return (React.createElement(FacebookConnectionContext.Provider, { value: { login, status, authData } }, children));
};
//# sourceMappingURL=facebookConnectionContext.js.map